/**
 * The curated display label for a model id, in ONE place (R-1b-8).
 *
 * A model id is a vendor string ("claude-opus-5", "gpt-5.6-luna"). Every
 * surface a person reads shows a clean family label instead: the picker's rows,
 * the composer pill, and now the settings cards, which speak the same names out
 * loud on the Line. The map lived in apps/web's model-catalog.ts, where the
 * server could not reach it; it lives here so both apps read one table and a
 * model renamed in one place cannot be spoken by its id in the other.
 *
 * The web's `labelForModel` reads this and keeps its own "Sonnet 5.5" fallback
 * for a picker row that must always render something. A server card has no such
 * licence: `labelForModelId` answers null for an id with no label, and the
 * caller then speaks about the model without naming it.
 *
 * Blurbs, brands, group order and pickability stay in the web catalog: they are
 * picker copy, not names a card ever says.
 */
import { KNOWN_MODEL_IDS, isModelAvailable } from '../models/capabilities.js';

/** The labels of the ids currently offered in the picker. */
export const MODEL_FAMILY_LABELS: Readonly<Record<string, string>> = {
  'claude-fable-5-1': 'Fable 5.1',
  'claude-opus-5-5': 'Opus 5.5',
  'claude-sonnet-5-5': 'Sonnet 5.5',
  'claude-haiku-5-5': 'Haiku 5.5',
  'gpt-6-astra': 'GPT-6 Astra',
  'gpt-6.1-sol': 'GPT-6.1 Sol',
  'gpt-6-luna': 'GPT-6 Luna',
  'grok-4.7': 'Grok 4.7',
  'gemini-4-argon': 'Gemini 4 Argon',
  'gemini-3.8-flash': 'Flash 3.8',
};

/**
 * The line-up: the ids a person can pick today, in picker order. Derived from the
 * offered table above, never a second hand list: an id the registry knows and does
 * not gate (`available: false`), the same filter the web picker applies. A model
 * that leaves the picker moves to HISTORICAL_MODEL_LABELS and leaves this list with
 * it. The box's Agent tool offers a subagent exactly these (founder, 2026-09-25:
 * "remove models with unlisted ids, the subagents should never get them").
 */
export const LISTED_MODEL_IDS: readonly string[] = Object.keys(MODEL_FAMILY_LABELS).filter(
  (id) => KNOWN_MODEL_IDS.includes(id) && isModelAvailable(id),
);

/**
 * Models off the picker but still on historical tasks, and still billable: the
 * label is kept so an old task, a usage row or a stored preference reads as a
 * name rather than as an id. Moved here verbatim from the web catalog, reasons
 * and all.
 */
export const HISTORICAL_MODEL_LABELS: Readonly<Record<string, string>> = {
  // GPT-5.4/5.5 delisted 2026-07-10 (GPT-5.6 Sol/Terra/Luna live). Labels kept for
  // historical tasks; SEED rows + pricing entries also remain so old tasks resume
  // and re-price correctly.
  'gpt-5.5': 'GPT-5.5',
  'gpt-5.4': 'GPT-5.4',
  'gpt-5.4-mini': 'GPT-5.4 mini',
  // Opus 4.8 delisted 2026-07-24 (superseded by Opus 5). Label kept so a task
  // locked to it still reads "Opus 4.8" instead of falling back to Sonnet.
  'claude-opus-4-8': 'Opus 4.8',
  // GPT-5.6 Sol and Luna delisted 2026-09-23 (superseded by GPT-6 Sol and Luna);
  // Terra retired the same day (its successor is GPT-6 Sol). Labels kept so a
  // task locked to one still reads its own name.
  'gpt-5.6-sol': 'GPT-5.6 Sol',
  'gpt-5.6-terra': 'GPT-5.6 Terra',
  'gpt-5.6-luna': 'GPT-5.6 Luna',
  // Opus 5 delisted 2026-09-22 (superseded by Opus 5.5). Same reason: a task locked
  // to it still reads "Opus 5"; its SEED row and pricing entry also remain.
  'claude-opus-5': 'Opus 5',
  // Fable 5 delisted 2026-09-01 (superseded by Fable 5.1). Same reason: a task
  // locked to it must still read "Fable 5", not fall back to Sonnet. Its SEED row
  // and pricing entry also remain so old tasks resume and re-price correctly.
  'claude-fable-5': 'Fable 5',
  // DeepSeek V4 delisted 2026-08-24 for the Google OAuth review window: the
  // reviewer's template re-fires on the NAME regardless of the Fireworks US
  // hosting (three identical letters). Both stay registry-known + billable
  // (Fireworks-served); restore on approval.
  'deepseek-v4-pro': 'DeepSeek V4 Pro',
  'deepseek-v4-flash': 'DeepSeek V4 Flash',
  // GLM delisted 2026-08-25 (founder: off the picker until the Google
  // verification clears; removed, never deleted). Fireworks-served, registry-
  // known, billable; historical tasks keep the label. Restore on approval.
  'glm-5.2': 'GLM 5.2',
  // Sonnet 5 delisted 2026-09-29 (superseded by Sonnet 5.5), Grok 4.5 and 4.6 the
  // same day (superseded by Grok 4.7). A task locked to one still reads its own
  // name; SEED rows and pricing entries remain so old tasks resume and re-price.
  'claude-sonnet-5': 'Sonnet 5',
  'grok-4.5': 'Grok 4.5',
  'grok-4.6': 'Grok 4.6',
  // GPT-6 Sol delisted 2026-09-30 (superseded by GPT-6.1 Sol). A task locked to it
  // still reads its own name; its SEED row and pricing entry also remain.
  'gpt-6-sol': 'GPT-6 Sol',
  // Haiku 4.5 removed 2026-10-07 (Haiku 5.5 runs wherever it is named). A past turn or a
  // usage row on it still reads its own name; its SEED row and pricing entry also remain.
  'claude-haiku-4-5': 'Haiku 4.5',
};

/** The curated name for a model id: the offered set first, then the historical one. Null when nothing names it. */
export function labelForModelId(id: string | null | undefined): string | null {
  if (!id) return null;
  return MODEL_FAMILY_LABELS[id] ?? HISTORICAL_MODEL_LABELS[id] ?? null;
}

/** The id a person's own words name, matched against the labels case-insensitively. Null when no label is that. */
export function modelIdForLabel(said: string): string | null {
  const q = said.trim().toLowerCase();
  if (!q) return null;
  for (const [id, label] of [...Object.entries(MODEL_FAMILY_LABELS), ...Object.entries(HISTORICAL_MODEL_LABELS)]) {
    if (label.toLowerCase() === q) return id;
  }
  return null;
}
